import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry error tracking
 *
 * Features:
 * - Automatic error capturing with stack traces
 * - Browser tracing for performance monitoring
 * - Session replay for debugging (10% sample rate)
 * - User context from Firebase Auth
 * - Only enabled in production
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.MODE

  // Don't initialize in development or if DSN is missing
  if (environment === 'development' || !dsn) {
    console.log('Sentry: Skipped initialization (development mode or missing DSN)')
    return
  }

  Sentry.init({
    dsn,
    environment,

    // Integrations
    integrations: [
      // Automatic browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Session replay for debugging (captures user interactions)
      Sentry.replayIntegration({
        // Only capture sessions that have errors
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    // For 1 daily user, we can afford to trace all transactions
    tracesSampleRate: 1.0,

    // Session Replay
    // Capture 10% of all sessions for general monitoring
    replaysSessionSampleRate: 0.1,
    // Capture 100% of sessions with errors for debugging
    replaysOnErrorSampleRate: 1.0,

    // Filter configuration
    beforeSend(event, hint) {
      const error = hint.originalException as Error

      // Only filter out errors that are clearly from browser extension internals
      // (not errors that affect user experience like Google Translate DOM issues)
      if (error?.message?.includes('chrome-extension://') ||
          error?.message?.includes('moz-extension://') ||
          error?.message?.includes('top.GLOBALS')) {
        console.warn('Sentry: Filtered browser extension internal error')
        return null // Don't send to Sentry
      }

      return event
    },

    // Additional options
    ignoreErrors: [
      // Only filter internal browser extension errors
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
    ],
  })

  console.log('Sentry: Initialized successfully')
}

/**
 * Set user context for Sentry error reports
 * Call this after user authentication
 */
export function setSentryUser(userId: string | null, email?: string | null) {
  if (userId) {
    Sentry.setUser({
      id: userId,
      email: email || undefined,
    })
  } else {
    // Clear user context on logout
    Sentry.setUser(null)
  }
}

/**
 * Manually capture an exception
 * Use this for caught errors that you want to track
 */
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('additional_info', context)
  }
  Sentry.captureException(error)
}

/**
 * Manually capture a message
 * Use this for non-error events you want to track
 */
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level)
}
